import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPolicy() {
  const updated = "December 2025";
  return (
    <div className="min-h-screen bg-[var(--c4p-ivory)]">
      <div className="mx-auto max-w-3xl px-6 py-16 lg:px-0 lg:py-24">
        <Link
          to="/"
          data-testid="back-home-link"
          className="inline-flex items-center gap-2 font-sans-ui text-[13px] font-semibold text-[var(--c4p-forest)] hover:text-[var(--c4p-orange)]"
        >
          <ArrowLeft size={14} /> Back to home
        </Link>

        <h1 className="font-display mt-8 text-[44px] leading-[1.02] sm:text-[60px]">
          Privacy{" "}
          <span className="font-display-italic text-[var(--c4p-orange)]">Policy.</span>
        </h1>
        <p className="mt-4 font-sans-ui text-[13px] text-[var(--c4p-muted)]">
          Last updated: {updated}
        </p>

        <div className="mt-10 space-y-8 font-sans-ui text-[15px] leading-relaxed text-[var(--c4p-ink)]/85">
          <section>
            <h2 className="font-display text-[22px] text-[var(--c4p-forest)]">1. Who we are</h2>
            <p className="mt-3">
              Cash 4 Properties ("we," "us," or "our") is a local real-estate
              investment company operated by Triston, serving Eastern North
              Carolina. You can reach us at{" "}
              <a className="underline" href="tel:+19102125006">(910) 212-5006</a> or{" "}
              <a className="underline" href="mailto:triston@cash-4-properties.com">
                triston@cash-4-properties.com
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="font-display text-[22px] text-[var(--c4p-forest)]">2. Information we collect</h2>
            <p className="mt-3">
              When you submit a form on this website, we collect:
            </p>
            <ul className="mt-3 list-disc space-y-1 pl-6">
              <li>Your name, phone number, and email address</li>
              <li>The address of the property you want to sell</li>
              <li>Optional details: condition, timeline, reason for selling</li>
              <li>Technical data automatically (IP address, browser, device, time)</li>
              <li>
                A reCAPTCHA risk score from Google to block spam submissions
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-[22px] text-[var(--c4p-forest)]">3. How we use it</h2>
            <ul className="mt-3 list-disc space-y-1 pl-6">
              <li>To respond to your cash-offer request and contact you about your property</li>
              <li>To research comparable sales and generate an offer</li>
              <li>To coordinate closing, title, and payment if you accept an offer</li>
              <li>To keep records required by law</li>
              <li>To improve our website and marketing (aggregate, non-identifying)</li>
            </ul>
            <p className="mt-3">
              We will not use your information for unrelated marketing without
              your consent.
            </p>
          </section>

          <section>
            <h2 className="font-display text-[22px] text-[var(--c4p-forest)]">4. Communication consent (TCPA)</h2>
            <p className="mt-3">
              By submitting a form and checking the consent box, you expressly
              consent to be contacted by Cash 4 Properties at the phone number
              and email you provide — including by call, SMS text, voicemail,
              or automated/pre-recorded messages — even if that number is on a
              Do Not Call list. Message and data rates may apply. Consent is
              not required to receive a cash offer; you can call us directly at
              (910) 212-5006. You may opt out at any time by replying STOP to a
              text or emailing us.
            </p>
          </section>

          <section>
            <h2 className="font-display text-[22px] text-[var(--c4p-forest)]">5. Who we share data with</h2>
            <p className="mt-3">
              We do not sell your personal information. We share it only with:
            </p>
            <ul className="mt-3 list-disc space-y-1 pl-6">
              <li>Service providers who help us operate (email delivery via Resend; spam protection via Google reCAPTCHA; database hosting)</li>
              <li>Title and closing companies if you accept an offer</li>
              <li>Law enforcement or regulators if we are legally required</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-[22px] text-[var(--c4p-forest)]">6. Data retention</h2>
            <p className="mt-3">
              We keep submitted information for as long as needed to serve you
              and meet legal obligations (typically up to 7 years for closed
              transactions). You can request earlier deletion by emailing us.
            </p>
          </section>

          <section>
            <h2 className="font-display text-[22px] text-[var(--c4p-forest)]">7. Your rights</h2>
            <p className="mt-3">
              You can ask us to access, correct, or delete the information we
              have about you. Email{" "}
              <a className="underline" href="mailto:triston@cash-4-properties.com">
                triston@cash-4-properties.com
              </a>{" "}
              and we'll respond within 30 days.
            </p>
          </section>

          <section>
            <h2 className="font-display text-[22px] text-[var(--c4p-forest)]">8. Cookies & analytics</h2>
            <p className="mt-3">
              Our site may use essential cookies to remember your form progress
              and Google reCAPTCHA scripts to prevent spam. We may add
              analytics (such as Google Analytics) in the future to measure
              traffic; when we do, we'll update this page.
            </p>
          </section>

          <section>
            <h2 className="font-display text-[22px] text-[var(--c4p-forest)]">9. Children</h2>
            <p className="mt-3">
              This site is not intended for anyone under 18. We do not
              knowingly collect information from minors.
            </p>
          </section>

          <section>
            <h2 className="font-display text-[22px] text-[var(--c4p-forest)]">10. Changes</h2>
            <p className="mt-3">
              We may update this Policy. The "Last updated" date above will
              reflect the latest revision.
            </p>
          </section>

          <section>
            <h2 className="font-display text-[22px] text-[var(--c4p-forest)]">11. Contact</h2>
            <p className="mt-3">
              Questions? Email{" "}
              <a className="underline" href="mailto:triston@cash-4-properties.com">
                triston@cash-4-properties.com
              </a>{" "}
              or call (910) 212-5006.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
